import type { AuthProvider } from '@nicheaim/nicheaim-timetracking-webcomponent/tracker';
import { RefObject, useLayoutEffect } from 'react';
import { useNicheAimTimeTracking } from './NicheAimTimeTrackingProvider';

interface EmbeddedElement extends HTMLElement {
  serverUrl: string;
  authProvider?: AuthProvider;
  invalidateAuthentication(): void;
}
/** Shared host lifecycle only; every element retains its own requests, token and SSE. */
export function useNicheAimElement<T extends EmbeddedElement>(
  ref: RefObject<T>,
  configure: (element: T) => void
) {
  const integration = useNicheAimTimeTracking();
  const { enabled, serverUrl, authProvider, tenantCode, userId } = integration;
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    if (!enabled || !serverUrl || !authProvider) {
      element.authProvider = undefined;
      element.invalidateAuthentication();
      return undefined;
    }
    configure(element);
    element.serverUrl = serverUrl;
    element.authProvider = authProvider;
    element.invalidateAuthentication();
    return () => {
      element.authProvider = undefined;
      element.invalidateAuthentication();
    };
  }, [ref, configure, enabled, serverUrl, authProvider, tenantCode, userId]);
  return integration;
}
